import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";

import DashboardLayout from "./DashboardLayout";
import { apiFetch } from "../utils/apiClient";
import { clearAuthUser, getAuthUser } from "../utils/auth";
import {
  clearSelectedPackage,
  getSelectedPackage,
} from "../utils/packageSelection";
import { getReferralCode } from "../utils/referralSession";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value || 0);

const PaymentShell = ({
  breadcrumb = ["Dashboard", "Packages", "Credit or Debit Card", "Payment Verification"],
  title = "Credit or Debit Card",
  subtitle = "Choose one of the payment methods below.",
  onBack,
  onBreadcrumbNavigate,
  children,
}) => (
  <div className="relative min-h-[calc(100vh-140px)] overflow-hidden rounded-3xl px-6 py-10 text-white sm:px-10 lg:px-12">
    <div className="relative z-10 mx-auto flex w-full max-w-8xl flex-col gap-10">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <nav className="text-sm text-white/45">
            {breadcrumb.map((entry, index) => {
              const item =
                typeof entry === "string"
                  ? { label: entry }
                  : entry && typeof entry === "object"
                    ? entry
                    : { label: String(entry) };
              const isLast = index === breadcrumb.length - 1;
              const isClickable = Boolean(item.to) && !isLast;
              const key = `${item.label}-${index}`;

              return (
                <span key={key} className="inline-flex items-center">
                  {isClickable ? (
                    <button
                      type="button"
                      onClick={() => onBreadcrumbNavigate?.(item.to)}
                      className="rounded px-1 text-white/65 transition hover:text-white hover:underline hover:underline-offset-4"
                    >
                      {item.label}
                    </button>
                  ) : (
                    <span className={isLast ? "text-white" : undefined}>{item.label}</span>
                  )}
                  {index < breadcrumb.length - 1 && (
                    <span className="mx-2 text-white/25">›</span>
                  )}
                </span>
              );
            })}
          </nav>
          <h1 className="mt-3 text-3xl font-semibold sm:text-4xl">{title}</h1>
          <p className="mt-2 max-w-xl text-sm text-white/60 sm:text-base">{subtitle}</p>
        </div>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="self-start rounded-xl border border-white/15 bg-white/5 px-5 py-2 text-sm font-semibold text-white/70 transition hover:border-white/30 hover:text-white"
          >
            Back
          </button>
        )}
      </header>

      <section className="grid gap-12 lg:grid-cols-[minmax(0,520px),minmax(260px,1fr)]">
        <div className="space-y-8">{children}</div>
        <div className="relative hidden items-center justify-center lg:flex">
          <div className="absolute inset-0 rounded-[320px] " />
          <img
            src="/images/payment.png"
            alt="Payment terminal illustration"
            className="relative z-10 h-[600px] w-auto ]"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        </div>
      </section>
    </div>
  </div>
);

const OrderSummary = ({ basePrice = 0, discount = 0, finalAmount = 0 }) => (
  <div className="space-y-3 text-sm text-white/65">
    <div className="flex items-center justify-between">
      <span>Tax</span>
      <span>{formatCurrency(0)}</span>
    </div>
    <div className="flex items-center justify-between text-white">
      <span>Subtotal</span>
      <span>{formatCurrency(basePrice)}</span>
    </div>
    {discount > 0 && (
      <div className="flex items-center justify-between text-[#A98CFF]">
        <span>Discount</span>
        <span>-{formatCurrency(discount)}</span>
      </div>
    )}
    <div className="h-px bg-white/10" />
    <div className="flex items-center justify-between text-base font-semibold text-white">
      <span>Order Total</span>
      <span>{formatCurrency(finalAmount)}</span>
    </div>
  </div>
);

export default function PaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const authUser = useMemo(() => getAuthUser(), []);
  const breadcrumbItems = useMemo(
    () => [
      { label: "Dashboard", to: "/dashboard" },
      { label: "Packages", to: "/dashboard/packages" },
      { label: "Credit or Debit Card", to: "/checkout" },
      { label: "Payment Verification" },
    ],
    [],
  );

  const handleSignOut = () => {
    clearAuthUser();
    navigate("/login", { replace: true });
  };

  const [selectedPackage, setSelectedPackage] = useState(
    () => location.state?.package || getSelectedPackage(),
  );

  useEffect(() => {
    if (location.state?.package) {
      setSelectedPackage(location.state.package);
    } else if (!selectedPackage) {
      setSelectedPackage(getSelectedPackage());
    }
  }, [location.state?.package, selectedPackage]);

  useEffect(() => {
    if (selectedPackage) {
      clearSelectedPackage();
    }
  }, [selectedPackage]);
  const basePrice = useMemo(() => {
    if (!selectedPackage?.price) return 0;
    const numeric = Number(String(selectedPackage.price).replace(/[^0-9.]/g, ""));
    return Number.isFinite(numeric) ? numeric : 0;
  }, [selectedPackage]);

  const [finalAmount, setFinalAmount] = useState(basePrice);
  const [currency] = useState("usd");
  const [discount, setDiscount] = useState(0);
  const initialReferralCode = useMemo(() => getReferralCode(), []);
  const [referralCode] = useState(initialReferralCode);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setFinalAmount(basePrice);
    setDiscount(0);
  }, [basePrice]);

  const handleMockSubmit = async (event) => {
    event.preventDefault();

    if (!selectedPackage) {
      setError("Please select a package before starting a payment.");
      return;
    }

    setProcessing(true);
    setError("");

    try {
      // Create PayPal order - payment will go to admin's PayPal account
      const response = await apiFetch("/payments/paypal/create-order", {
        method: "POST",
        data: {
          amount: basePrice,
          currency: currency.toUpperCase(),
          packageName: selectedPackage.title,
          referrerCode: referralCode.trim() || undefined,
        },
      });

      if (response.approvalUrl) {
        // Redirect to PayPal for payment approval
        // User will complete payment on PayPal, then redirect back
        window.location.href = response.approvalUrl;
      } else {
        setError("Failed to create payment order. Please try again.");
        setProcessing(false);
      }
    } catch (paypalError) {
      console.error("PayPal order creation error:", paypalError);
      setError(paypalError?.data?.message || paypalError.message || "Unable to process payment.");
      setProcessing(false);
    }
  };

  const renderSelectedPackage = () => {
    if (!selectedPackage) {
      return (
        <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 px-6 py-5 text-sm text-yellow-200">
          No package information found. Please go back and select a package.
        </div>
      );
    }

    return null;
  };

  const renderMockCheckout = () => {
    return (
      <PaymentShell
        breadcrumb={breadcrumbItems}
        onBack={() => navigate(-1)}
        onBreadcrumbNavigate={(path) => navigate(path)}
      >
        {error && (
          <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {renderSelectedPackage()}

        {selectedPackage && (
          <div className="rounded-3xl border border-white/10 bg-black/35 p-8 shadow-[0_28px_90px_rgba(8,5,19,0.55)] backdrop-blur-sm w-full max-w-md sm:max-w-lg mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-4 text-xs font-semibold uppercase tracking-[0.25em] text-white/45">
              <span>Payment method</span>
              <div className="inline-flex overflow-hidden rounded-full border border-white/15 bg-black/60 p-1 text-[11px] text-white/70">
                <span className="rounded-full px-5 py-2 bg-gradient-to-r from-[#8E44FF] to-[#775BFF] text-white shadow-[0_8px_20px_rgba(126,85,255,0.35)]">
                  Debit / Credit Card
                </span>
              </div>
            </div>

            <div className="mt-6 space-y-6">
              <p className="text-sm text-white/65">
                Your payment will be completed securely on our trusted payment partner&apos;s
                checkout page. You will be redirected to choose your payment method (debit or
                credit card). We do not see or store your card details.
              </p>

              <OrderSummary basePrice={basePrice} discount={discount} finalAmount={finalAmount} />

              <button
                type="button"
                onClick={handleMockSubmit}
                disabled={processing || !selectedPackage}
                className="w-full rounded-lg bg-gradient-to-r from-[#713FFF] to-[#B84CFF] px-6 py-4 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(142,73,255,0.35)] transition hover:from-[#8351ff] hover:to-[#c15aff] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {processing
                  ? "Redirecting to secure checkout…"
                  : `Pay with debit / credit card · ${formatCurrency(finalAmount)}`}
              </button>
            </div>
          </div>
        )}
      </PaymentShell>
    );
  };

  return (
    <DashboardLayout user={authUser} onSignOut={handleSignOut} activeKey="packages">
      {() => renderMockCheckout()}
    </DashboardLayout>
  );
}
